import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button, Col, Empty, Pagination, Result, Row, Spin, Tabs, Tag, Typography } from 'antd';
import { ToolOutlined } from '@ant-design/icons';
import {
    EQUIPMENT_SEARCH_ENABLED,
    PREVIEW_EQUIPMENT,
    PREVIEW_JOBS,
    PREVIEW_PRODUCTS,
    RESULTS_PAGE_SIZE,
    SEARCH_TYPES,
} from '../config/search';
import { searchProducts } from '../servics/search/productSearchApi';
import { searchJobOffers } from '../servics/search/jobOfferSearchApi';
import { searchEquipment } from '../servics/search/equipmentSearchApi';
import EquipmentCard from '../ass_components/EquipmentCard';
import { setMarketplaceProducts } from '../store/reducers/marketplaceSlice';
import ProductDiscoveryCard from '../ass_components/product-instance-components/ProductDiscoveryCard';
import CATEGORY_IMAGES from '../config/categoryImages';
import { formatAmount } from '../constants/marketplaceCheckout';
import JobOfferResultCard from '../ass_components/search/JobOfferResultCard';
import { fill, labelOf, plural, searchTexts } from '../ass_components/search/searchText';
import '../css/search.css';

const { Title, Text } = Typography;

const IDLE = { loading: false, error: false, items: [], total: 0 };

/** Taille demandée selon l'onglet : page entière, aperçu (« Tout ») ou simple compteur. */
const sizeFor = (type, own, preview) => {
    if (type === own) return RESULTS_PAGE_SIZE;
    return type === 'all' ? preview : 1;
};

/**
 * /search?q=&type=all|products|jobs|equipment&category=&subCategory=&page=
 *
 * Produits (agri-product-platform) et offres de travaux (agri-farming-operations-platform)
 * du pays d'activité choisi ; « Équipements » reste « bientôt disponible » tant que
 * EQUIPMENT_SEARCH_ENABLED est à false (pas encore de backend).
 */
const SearchResults = () => {
    const dispatch = useDispatch();
    const [params, setParams] = useSearchParams();
    const lang = useSelector((state) => state.lang?.lang) || 'FR';
    const t = searchTexts(lang);
    const country = useSelector((state) => state.country?.activityCountry?.value);

    const q = (params.get('q') || '').trim();
    const requestedType = params.get('type');
    const type = SEARCH_TYPES.includes(requestedType) ? requestedType : 'all';
    const category = params.get('category') || undefined;
    const subCategory = params.get('subCategory') || undefined;
    const page = Math.max(1, Number(params.get('page')) || 1);

    const [products, setProducts] = useState({ ...IDLE, matched: [] });
    const [jobs, setJobs] = useState({ ...IDLE, currency: null });
    const [equipment, setEquipment] = useState(IDLE);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!country) return undefined;
        let cancelled = false;

        setProducts((previous) => ({ ...previous, loading: true, error: false }));
        searchProducts({
            q, country, category, subCategory,
            page: type === 'products' ? page - 1 : 0,
            size: sizeFor(type, 'products', PREVIEW_PRODUCTS),
        })
            .then((result) => {
                if (cancelled) return;
                setProducts({ loading: false, error: false, ...result });
                // ProductInstanceDetails retrouve le lot dans le store, comme depuis le catalogue.
                dispatch(setMarketplaceProducts(result.items));
            })
            .catch(() => !cancelled && setProducts({ ...IDLE, matched: [], error: true }));

        // Les offres de travaux n'ont pas de catégorie produit : sans saisie, un filtre
        // catégorie / sous-catégorie ne concerne que les produits.
        const jobsApply = !(category || subCategory) || q;
        if (jobsApply) {
            setJobs((previous) => ({ ...previous, loading: true, error: false }));
            searchJobOffers({
                q, country,
                page: type === 'jobs' ? page - 1 : 0,
                size: sizeFor(type, 'jobs', PREVIEW_JOBS),
            })
                .then((result) => !cancelled && setJobs({ loading: false, error: false, ...result }))
                .catch(() => !cancelled && setJobs({ ...IDLE, currency: null, error: true }));
        } else {
            setJobs({ ...IDLE, currency: null });
        }

        // Aucun appel tant que la vente d'équipements n'a pas de backend (config/search.js).
        if (EQUIPMENT_SEARCH_ENABLED) {
            setEquipment((previous) => ({ ...previous, loading: true, error: false }));
            searchEquipment({
                q, country,
                page: type === 'equipment' ? page - 1 : 0,
                size: sizeFor(type, 'equipment', PREVIEW_EQUIPMENT),
            })
                .then((result) => !cancelled && setEquipment({ loading: false, error: false, ...result }))
                .catch(() => !cancelled && setEquipment({ ...IDLE, error: true }));
        }
        return () => {
            cancelled = true;
        };
    }, [q, country, type, category, subCategory, page, reloadKey, dispatch]);

    const show = (nextType, nextPage) => {
        const next = new URLSearchParams(params);
        next.set('type', nextType);
        if (nextPage && nextPage > 1) next.set('page', String(nextPage));
        else next.delete('page');
        setParams(next);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const retry = <Button onClick={() => setReloadKey((key) => key + 1)}>{t.retry}</Button>;

    const productGrid = (limit) => {
        if (products.loading) return <Spin className="search-spin" />;
        if (products.error) return <Alert type="warning" showIcon message={t.error} action={retry} />;
        if (!products.items.length) return <Empty description={t.no_products} />;
        return (
            <Row gutter={[16, 16]}>
                {products.items.slice(0, limit).map((product) => (
                    <Col xs={24} md={12} lg={8} key={product.id}>
                        <ProductDiscoveryCard
                            product={product}
                            categoryImage={CATEGORY_IMAGES[product.subCategoryName] || CATEGORY_IMAGES.default}
                        />
                    </Col>
                ))}
            </Row>
        );
    };

    const jobList = (limit) => {
        if (jobs.loading) return <Spin className="search-spin" />;
        if (jobs.error) return <Alert type="warning" showIcon message={t.error} action={retry} />;
        if (!jobs.items.length) return <Empty description={t.no_jobs} />;
        return (
            <Row gutter={[16, 16]}>
                {jobs.items.slice(0, limit).map((offer) => (
                    <Col xs={24} md={12} lg={8} key={`${offer.tenantCode}:${offer.id}`}>
                        <JobOfferResultCard offer={offer} currency={jobs.currency} t={t} />
                    </Col>
                ))}
            </Row>
        );
    };

    const pager = (total) => total > RESULTS_PAGE_SIZE && (
        <Pagination
            className="search-pagination"
            current={page}
            pageSize={RESULTS_PAGE_SIZE}
            total={total}
            showSizeChanger={false}
            onChange={(nextPage) => show(type, nextPage)}
        />
    );

    const equipmentList = (limit) => {
        if (!EQUIPMENT_SEARCH_ENABLED) {
            return <Result icon={<ToolOutlined />} title={t.equipment_soon_title} subTitle={t.equipment_soon_text} />;
        }
        if (equipment.loading) return <Spin className="search-spin" />;
        if (equipment.error) return <Alert type="warning" showIcon message={t.error} action={retry} />;
        if (!equipment.items.length) return <Empty description={t.no_equipment} />;
        return (
            <Row gutter={[16, 16]}>
                {equipment.items.slice(0, limit).map((item) => (
                    <Col xs={24} md={12} lg={8} key={item.id}>
                        <EquipmentCard
                            id={item.id}
                            image={item.imageUrl}
                            title={item.title}
                            subtitle={item.sellerName}
                            price={formatAmount(item.price, item.currency)}
                        />
                    </Col>
                ))}
            </Row>
        );
    };

    const section = (title, count, key, body) => (
        <section className="search-section">
            <div className="search-section-head">
                <Title level={4}>{title} <Text type="secondary">({count})</Text></Title>
                {count > 0 && <Button type="link" onClick={() => show(key)}>{t.see_all}</Button>}
            </div>
            {body}
        </section>
    );

    const counted = (state, key) => (state.loading ? key : `${key} (${state.total})`);

    const tabs = [
        {
            key: 'all',
            label: t.type_all,
            children: (
                <>
                    {section(t.type_products, products.total, 'products', productGrid(PREVIEW_PRODUCTS))}
                    {section(t.type_jobs, jobs.total, 'jobs', jobList(PREVIEW_JOBS))}
                    {EQUIPMENT_SEARCH_ENABLED
                        && section(t.type_equipment, equipment.total, 'equipment', equipmentList(PREVIEW_EQUIPMENT))}
                </>
            ),
        },
        {
            key: 'products',
            label: counted(products, t.type_products),
            children: (
                <>
                    {!products.loading && !products.error && (
                        <Text type="secondary" className="search-count">{plural(t, 'products', products.total)}</Text>
                    )}
                    {productGrid(RESULTS_PAGE_SIZE)}
                    {pager(products.total)}
                </>
            ),
        },
        {
            key: 'jobs',
            label: counted(jobs, t.type_jobs),
            children: (
                <>
                    {!jobs.loading && !jobs.error && (
                        <Text type="secondary" className="search-count">{plural(t, 'jobs', jobs.total)}</Text>
                    )}
                    {jobList(RESULTS_PAGE_SIZE)}
                    {pager(jobs.total)}
                </>
            ),
        },
        {
            key: 'equipment',
            label: EQUIPMENT_SEARCH_ENABLED ? counted(equipment, t.type_equipment) : t.type_equipment,
            children: (
                <>
                    {equipmentList(RESULTS_PAGE_SIZE)}
                    {EQUIPMENT_SEARCH_ENABLED && pager(equipment.total)}
                </>
            ),
        },
    ];

    return (
        <div className="search-page container-xxl">
            <Title level={2} className="search-title">
                {q ? fill(t.results_title, { q }) : t.results_title_browse}
            </Title>

            {products.matched?.length > 0 && (
                <div className="search-matched">
                    <Text type="secondary">{t.recognised}</Text>
                    {products.matched.map((entry) => (
                        <Tag key={`${entry.level}:${entry.code}`} color="gold">
                            {labelOf(entry, lang)} · {t[`level_${entry.level}`]}
                        </Tag>
                    ))}
                </div>
            )}

            {!country ? (
                <Alert type="info" showIcon message={t.country_required} />
            ) : (
                <Tabs activeKey={type} onChange={(key) => show(key)} items={tabs} destroyOnHidden />
            )}
        </div>
    );
};

export default SearchResults;
